import axios from 'axios'
import React from 'react'
import { baseUrl } from '../assets/Animations'
import './student-modal-2023.css'
import { MdClose } from 'react-icons/md'
import LeftGutterStudentModal2023 from './Gutters/LeftGutterStudentModal2023/LeftGutterStudentModal2023'
import CenterGutterStudentModal2023 from './Gutters/CenterGutterStudentModal2023/CenterGutterStudentModal2023'
import { getRequestHeaders } from '../api'
import NoteAreaFall2023 from '../StudentOnboardingCardModalFall2023/NoteAreaFall2023/NoteAreaFall2023'
import MagicScheduler from '../StudentOnboardingCardModalFall2023/MagicScheduler/MagicScheduler'

const StudentModal2023 = ({
    familyId,
    setStudentModal2023,
    userData,
    socket,
    createNoteFunc,
    deleteFamilyFromState,
    onFamilySaved,
}) => {
    const [family, setFamily] = React.useState({})
    const [originalFamily, setOriginalFamily] = React.useState(null)
    const [instrumentList, setInstrumentList] = React.useState([])
    const [teachers, setTeachers] = React.useState([])
    const [cancellations, setCancellations] = React.useState([])
    const [saving, setSaving] = React.useState(false)
    const [showTeacherAssignmentReasonPopup, setShowTeacherAssignmentReasonPopup] = React.useState(false)
    const [teacherAssignmentReason, setTeacherAssignmentReason] = React.useState('')
    const [teacherAssignmentNotes, setTeacherAssignmentNotes] = React.useState('')
    const [pendingTeacherChanges, setPendingTeacherChanges] = React.useState([])
    const [showMagicScheduler, setShowMagicScheduler] = React.useState(false)
    const [teacherDistanceSuggestions, setTeacherDistanceSuggestions] = React.useState([])
    const resolvedFamilyId = typeof familyId === 'object'
        ? familyId?.familyId
        : familyId
    const openedFromMagicScheduler = Boolean(
        typeof familyId === 'object' && familyId?.openedFromMagicScheduler
    )

    const cloneFamilySnapshot = (familyData) => JSON.parse(JSON.stringify(familyData))

    React.useEffect(() => {
        getFamilyInfo()
        getInstrumentList()
        getFamilyCancellations()
    }, [])

    React.useEffect(() => {
        if(family.familyId) {
            getSchoolsTeachers(family.school)
            setStudentModal2023({
                ...family,
                openedFromMagicScheduler,
            })
        }
    }, [family, openedFromMagicScheduler, setStudentModal2023])

    const getTeacherChanges = (previousFamily, updatedFamily) => {
        if(!previousFamily || !updatedFamily) {
            return []
        }

        const previousStudentsById = new window.Map(
            (previousFamily.students || []).map(student => [String(student.studentId || ''), student])
        )

        return (updatedFamily.students || []).reduce((changes, student) => {
            const previousStudent = previousStudentsById.get(String(student.studentId || ''))

            if(!previousStudent) {
                return changes
            }

            const previousTeacherId = String(previousStudent.teachersId || '')
            const nextTeacherId = String(student.teachersId || '')

            if(previousTeacherId === nextTeacherId) {
                return changes
            }

            if(!previousTeacherId) {
                return changes
            }

            changes.push({
                studentId: student.studentId,
                studentName: student.studentName || previousStudent.studentName || '',
                previousTeacherId,
                nextTeacherId
            })

            return changes
        }, [])
    }

    const resetTeacherAssignmentPrompt = () => {
        setShowTeacherAssignmentReasonPopup(false)
        setTeacherAssignmentReason('')
        setTeacherAssignmentNotes('')
        setPendingTeacherChanges([])
    }

    const saveFamily = (extraPayload = {}) => {
        setSaving(true)
        axios.put(
            `${baseUrl}/school-management/${family.familyId}`,
            {
                ...family,
                ...extraPayload
            },
            getRequestHeaders()
        )
        .then(res => {
            const responseFamily = res?.data?.family || res?.data
            const savedFamily = responseFamily && typeof responseFamily === 'object' && !Array.isArray(responseFamily)
                ? { ...family, ...responseFamily }
                : family
            resetTeacherAssignmentPrompt()
            onFamilySaved?.(savedFamily)
            setStudentModal2023(false)
        })
        .catch(err => console.log(err))
        .finally(() => setSaving(false))
    }

    const closeAndSaveModal = (id) => {
        if(id === 'student-modal-2023__wrapper' && !saving) {
            const teacherChanges = getTeacherChanges(originalFamily, family)

            if(teacherChanges.length > 0) {
                setPendingTeacherChanges(teacherChanges)
                setShowTeacherAssignmentReasonPopup(true)
                return
            }

            saveFamily()
        }
    }

    const closeAndDeleteModal = () => {
        axios.delete(`${baseUrl}/school-management/${family.familyId}`, getRequestHeaders())
        .then(res => {
            setStudentModal2023(false)
        })
        .catch(err => console.log(err))
    }

    const getFamilyInfo = async () => {
        axios.get(`${baseUrl}/school-management/${resolvedFamilyId}`, getRequestHeaders())
        .then(res => {
            setFamily(cloneFamilySnapshot(res.data))
            setOriginalFamily(cloneFamilySnapshot(res.data))
        })
        .catch(err => console.log(err))
    }

    const getSchoolsTeachers = async (school) => {
        await axios.get(`${baseUrl}/teachersbyschool/${school.toLowerCase()}`, getRequestHeaders())
        .then(res => {
            res.data.sort((a, b) => {
                let nameA = a.Name.toUpperCase()
                let nameB = b.Name.toUpperCase()
                if(nameA < nameB) {
                    return -1
                }
                if(nameA > nameB) {
                    return 1
                }
                return 0
            })
            setTeachers(res.data)
        })
        .catch(err => console.log(err))
    }

    const getInstrumentList = async () => {
        await axios.get(`${baseUrl}/inst`, getRequestHeaders())
        .then(res => {
            //alphabetize instrument list
            res.data.sort((a, b) => {
                let nameA = a.name.toUpperCase()
                let nameB = b.name.toUpperCase()
                if(nameA < nameB) {
                    return -1
                }
                if(nameA > nameB) {
                    return 1
                }
                return 0
            })
            setInstrumentList(res.data)
        })
        .catch(err => console.log(err))
    }

    const getFamilyCancellations = async () => {
        await axios.get(`${baseUrl}/cancellation/family/${resolvedFamilyId}`, getRequestHeaders())
        .then(res => {
            setCancellations(res.data)
        })
        .catch(err => console.log(err))
    }

    return (
        <div className='student-modal-2023__wrapper' id='student-modal-2023__wrapper' onClick={(e) => closeAndSaveModal(e.target.id)}>
            {saving && (
                <div className='student-modal-2023__saving-overlay'>
                    <div className='student-modal-2023__saving-card'>
                        <div className='student-modal-2023__saving-spinner' />
                        <div className='student-modal-2023__saving-title'>Saving family</div>
                        <div className='student-modal-2023__saving-subtitle'>Updating the family profile before closing.</div>
                    </div>
                </div>
            )}
            {showTeacherAssignmentReasonPopup && !saving && (
                <div className='student-modal-2023__reason-overlay'>
                    <div className='student-modal-2023__reason-card'>
                        <div className='student-modal-2023__reason-title'>Why did the teacher assignment change?</div>
                        <div className='student-modal-2023__reason-subtitle'>
                            This save changed the assigned teacher for {pendingTeacherChanges.length} student{pendingTeacherChanges.length === 1 ? '' : 's'}.
                        </div>
                        <div className='student-modal-2023__reason-student-list'>
                            {pendingTeacherChanges.map((change) => (
                                <div className='student-modal-2023__reason-student-chip' key={change.studentId || change.studentName}>
                                    {change.studentName || 'Student'}
                                </div>
                            ))}
                        </div>
                        <select
                            className='student-modal-2023__reason-select'
                            value={teacherAssignmentReason}
                            onChange={(e) => setTeacherAssignmentReason(e.target.value)}
                        >
                            <option value=''>Select a reason</option>
                            <option value='teacher-leaving'>Teacher Leaving</option>
                            <option value='fit'>Fit</option>
                            <option value='schedule'>Schedule</option>
                            <option value='parent-request'>Parent Request</option>
                            <option value='school-request'>School Request</option>
                            <option value='other'>Other</option>
                        </select>
                        <textarea
                            className='student-modal-2023__reason-notes'
                            placeholder='Optional notes'
                            value={teacherAssignmentNotes}
                            onChange={(e) => setTeacherAssignmentNotes(e.target.value)}
                        />
                        <div className='student-modal-2023__reason-actions'>
                            <button
                                className='student-modal-2023__reason-btn student-modal-2023__reason-btn--secondary'
                                onClick={resetTeacherAssignmentPrompt}
                            >
                                Cancel
                            </button>
                            <button
                                className='student-modal-2023__reason-btn student-modal-2023__reason-btn--primary'
                                disabled={!teacherAssignmentReason}
                                onClick={() => saveFamily({
                                    teacherAssignmentReason,
                                    teacherAssignmentNotes
                                })}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className='student-modal-2023__modal-system' id="student-modal-2023__modal-system" >
                <div className='student-modal-2023__x-column'>
                    <div className='student-modal-2023__x-column__x-wrapper' onClick={() => closeAndSaveModal('student-modal-2023__wrapper')}>
                        <MdClose size={23} color='#334155' />
                    </div>
                </div>
                {family.familyId ? <LeftGutterStudentModal2023
                    family={family}
                    setFamily={setFamily}
                    deleteFamilyFromState={deleteFamilyFromState}
                /> : <div>loading</div>}
                {family.familyId ? <CenterGutterStudentModal2023 
                    family={family} 
                    setFamily={setFamily}
                    instrumentList={instrumentList} 
                    teachers={teachers} 
                    cancellations={cancellations}
                    showMagicSchedulerButton={!openedFromMagicScheduler}
                    onOpenMagicScheduler={() => setShowMagicScheduler(true)}
                /> : <div>loading</div>}
                <div className='student-modal-2023__right-gutter'>
                {family.familyId && <NoteAreaFall2023 
                    modalInfo={{id: family.familyId, type: 'familyId', displayName: family.familyId}}  
                    userData={userData} 
                    createNoteFunc={createNoteFunc} 
                    school={family.school.toLowerCase()}
                    familyStudentCount={(family.students || []).length}
                />}
                </div>
            </div>
            {showMagicScheduler && family.familyId && (
                <MagicScheduler
                    modalInfo={{
                        todoId: `family-${family.familyId}`,
                        familyId: family.familyId,
                        todoName: family.familyName,
                        whichSchool: family.school,
                        studentList: Array.isArray(family.students) ? family.students : [],
                        todoDescription: family.initialAvailabilityNotes || '',
                    }}
                    familyProfile={family}
                    teacherDistanceSuggestions={teacherDistanceSuggestions}
                    setTeacherDistanceSuggestions={setTeacherDistanceSuggestions}
                    teachers={teachers}
                    userData={userData}
                    socket={socket}
                    onClose={() => setShowMagicScheduler(false)}
                />
            )}
        </div>
    )
}
export default StudentModal2023
